// frontend/src/components/ContactDocumentsAccordian.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-15
// Updated:      2026-09-10 10:27 BST
//
// Description:
//   UI-only accordion placeholder for contact-level documents/files.
//   • Designed to live in the Contact Function Tray below each contact row.
//   • No backend calls yet (to avoid assumptions) — ready to be wired once
//     job/document equivalents and endpoints are confirmed.
//
// Mobile Responsiveness (2026-09-10):
//   • Existing single-column accordion structure already works correctly on mobile.
//   • Increased the accordion header to a touch-safe minimum height.
//   • Added safe wrapping for long header/content text.
//   • Uses slightly tighter phone padding while retaining existing desktop spacing.
//   • Improved placeholder list indentation/readability on narrow screens.
//
// Operational Notes:
//   • Component/file name remains `ContactDocumentsAccordian` for compatibility.
//   • No backend/API behaviour exists in this placeholder and none is introduced here.
//   • Open/close state and ARIA wiring remain unchanged.

import React, { useState } from "react";

const ContactDocumentsAccordian = ({ contactId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      {/* Header */}
      <button
        type="button"
        className="flex min-h-[48px] w-full touch-manipulation items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-white bg-btnGeneralNormal"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        aria-controls={`contact-documents-panel-${contactId}`}
      >
        <span className="min-w-0 break-words">Contact Documents / Files</span>
        <span className="ml-2 flex-none" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open && (
        <div
          id={`contact-documents-panel-${contactId}`}
          className="space-y-2 border-t border-gray-200 bg-white p-3 text-xs text-gray-700 sm:p-4"
        >
          <p className="break-words text-sm font-semibold text-gray-800">
            Documents for contact #{contactId}
          </p>

          <p className="break-words text-xs text-gray-600">
            This is a placeholder for the contact document/files accordion.
            Once the document model & endpoints are confirmed, this panel will:
          </p>

          <ul className="list-outside list-disc space-y-1 pl-5 text-xs text-gray-600">
            <li className="break-words">
              List files linked to this contact (by contact_id).
            </li>
            <li className="break-words">
              Allow quick download/open in a new tab.
            </li>
            <li className="break-words">
              Show basic metadata (type, size, uploaded_at, job link, etc.).
            </li>
            <li className="break-words">
              Optionally support upload or “link existing” actions.
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ContactDocumentsAccordian;
